import type { ReactNode } from 'react';
import { insightFor, type GameMode, type Language } from '@lexicon/core';
import { Sheet } from './Sheet';
import { FlameIcon, ShareIcon, SparkIcon } from './icons';
import type { GameResult } from '../game/session';
import { useLanguage, useStrings } from '../state/AppContext';

export interface ResultSheetProps {
  open: boolean;
  onClose: () => void;
  mode: GameMode;
  answer: string;
  result: GameResult;
  /** `dayNumber` for dailies, the code for challenges. */
  shareId: string | number;
  onShare: () => void;
  ctaLabel: string;
  onCta: () => void;
  /** Extra line under the buttons (challenge result-send status). */
  footer?: ReactNode;
  /** Rendered directly under the subtitle (journey rung progress). */
  banner?: ReactNode;
  /**
   * Language of the answer being explained, when it is not the app's — a
   * challenge word from the other language (see GamePlay's
   * `wordLanguage`). The copy around it stays in the app's language.
   */
  answerLanguage?: Language;
}

function XpRow({ label, value, sign, strong = false }: {
  label: string; value: number; sign: '+' | '−'; strong?: boolean;
}) {
  return (
    <div className={[
      'flex items-center justify-between py-1 text-sm',
      strong ? 'border-t border-hairline pt-2 font-semibold' : 'text-dim',
    ].join(' ')}
    >
      <span>{label}</span>
      <span>{sign}{value}</span>
    </div>
  );
}

export function ResultSheet({
  open, onClose, mode, answer, result, shareId, onShare, ctaLabel, onCta, footer, banner,
  answerLanguage,
}: ResultSheetProps) {
  const t = useStrings();
  const appLanguage = useLanguage();
  const language = answerLanguage ?? appLanguage;
  // One heading per row a board can have (spec §10.1: up to eight at 7
  // letters). Index is `guessCount - 1`; anything beyond falls back.
  const heading = result.won
    ? (t.result.winHeadings[result.guessCount - 1] ?? t.result.solved)
    : t.result.lost;
  // Pre-epoch dailies (spec §3.3) carry a negative dayNumber — "Lexicon
  // #-6" reads as a typo, not a day count, so the number is dropped
  // entirely rather than shown signed (M3).
  const subtitle = mode === 'daily'
    ? (typeof shareId === 'number' && shareId < 0
      ? t.result.dailySubtitleNoNumber
      : t.result.dailySubtitle(shareId))
    : mode === 'journey' ? t.result.journeySubtitle(shareId)
      : t.result.challengeSubtitle(shareId);
  // The answer belongs to the language it was played in, and so does its
  // definition — `water` has an entry in both dictionaries.
  const insight = insightFor(answer, language);

  return (
    <Sheet open={open} title={heading} onClose={onClose}>
      <p className="-mt-2 mb-4 text-xs text-dim">{subtitle}</p>
      {banner ? <div className="mb-4">{banner}</div> : null}

      <div role="group" aria-label={t.result.answerLabel} className="mb-4 rounded-xl bg-black/20 p-4">
        <p className="text-center text-xl font-bold tracking-[0.3em]">{answer.toUpperCase()}</p>
        {insight ? (
          <>
            <p className="mt-2 text-sm">{insight.def}</p>
            {insight.origin
              ? <p className="mt-1 text-xs text-dim">{t.insight.origin(insight.origin)}</p>
              : null}
          </>
        ) : (
          <p className="mt-2 text-sm text-dim">{t.result.noEntry}</p>
        )}
      </div>

      <div role="group" aria-label={t.result.xpLabel} className="mb-4">
        <XpRow label={t.result.base} value={result.xpBase} sign="+" />
        {mode === 'daily' && result.xpStreakBonus > 0
          ? <XpRow label={t.result.streakBonus} value={result.xpStreakBonus} sign="+" />
          : null}
        {result.xpJourneyBonus
          ? <XpRow label={t.result.journeyComplete} value={result.xpJourneyBonus} sign="+" />
          : null}
        {result.xpHintPenalty > 0
          ? <XpRow label={t.result.hints} value={result.xpHintPenalty} sign="−" />
          : null}
        <XpRow label={t.result.xpLabel} value={result.xpAwarded} sign="+" strong />
      </div>

      {mode === 'daily' ? (
        <div className="mb-4 flex items-center gap-2 text-sm">
          <FlameIcon className="h-4 w-4 text-flame" />
          <span className="text-dim">{t.result.dayStreak}</span>
          <span className="ml-auto font-semibold">{result.streakAfter}</span>
        </div>
      ) : (
        <div className="mb-4 flex items-center gap-2 text-sm">
          <SparkIcon className="h-4 w-4 text-accent" />
          <span className="text-dim">
            {mode === 'journey' ? t.result.journeyNoStreak : t.result.challengeNoStreak}
          </span>
        </div>
      )}

      <div className="flex flex-col gap-2 pb-4">
        <button
          type="button"
          onClick={onShare}
          className="flex min-h-[52px] items-center justify-center gap-2 rounded-xl bg-grad-accent text-sm font-semibold text-white"
        >
          <ShareIcon className="h-5 w-5" />
          {t.result.share}
        </button>
        <button
          type="button"
          onClick={onCta}
          className="min-h-[52px] rounded-xl border border-hairline text-sm font-semibold"
        >
          {ctaLabel}
        </button>
        {footer}
      </div>
    </Sheet>
  );
}
